import { Controller } from "@hotwired/stimulus"

// Polls the persona status endpoint while the portrait (or the avatar) brews in
// a background job, then swaps the picture-area shimmer for the finished image
// IN PLACE — no full page reload. Same brewing family as daily_card, but a live
// generation is never abandoned: image paints now legitimately run 60–240s, so
// instead of a fixed attempt cap we keep polling (with a widening interval)
// until the job reaches a terminal state, and only give up — truthfully, with a
// "refresh to check" notice — well past the server-side paint budget.
//
// Progress shown is always real: the elapsed clock counts server time on the
// brewing row, the phase is the actual job state, and any evolving image is a
// genuine partial frame streamed from the provider — never a guessed percentage.
export default class extends Controller {
    static values = { url: String, frameUrl: String, brewing: Boolean, avatarPending: Boolean }

    // Interval schedule keyed on elapsed seconds: tight while a paint usually
    // lands, then relaxed to stay cheap on the long tail.
    static SCHEDULE = [
        { until: 30, every: 2500 },
        { until: 90, every: 5000 },
    ]
    static SLOW_INTERVAL = 10000
    // Hard wall (seconds) — comfortably past the 300s server paint budget. On
    // reaching it we stop and tell the truth rather than silently freezing.
    static HARD_WALL = 360

    connect() {
        if (!this.brewingValue) return
        this.startedAt = Date.now()
        this.lastPartial = null
        this.stopped = false
        // Did this polling session begin with an avatar crop in flight? If so,
        // the finished face shows in more places than the hero (chat sidebar
        // header, media strip), so we reload on completion instead of an
        // in-place hero swap. Seeded from the server and re-armed on any poll
        // that reports a pending crop, to survive the race where the crop lands
        // between connect() and the first poll.
        this.avatarWasPending = this.avatarPendingValue
        this.scheduleNext(0)
    }

    // Optimistic: the instant Keep is clicked, drop the Keep/Redo pills so the
    // decision reads as final immediately — before the redirect re-renders the
    // hero in its kept/cropping state. Progressive enhancement only; the
    // authoritative "no pills" is the server render we navigate to.
    keeping() {
        this.element.querySelectorAll(".persona-portrait-actions").forEach((el) => el.remove())
    }

    disconnect() {
        if (this.timeout) clearTimeout(this.timeout)
        this.stopped = true
    }

    async poll() {
        if (this.stopped) return
        let data
        try {
            const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
            data = await response.json()
        } catch (error) {
            console.error("Persona status poll error:", error)
            this.scheduleNext(this.elapsedSeconds())
            return
        }

        // Re-arm: a crop reported pending on any cycle means the completed face
        // must propagate beyond the hero, so remember it even if it clears next.
        if (data.avatar_pending) this.avatarWasPending = true

        // Terminal: the picture is settled (or the whole run failed).
        const settled = (data.ready && !data.avatar_pending) || data.failed
        if (settled) {
            // An avatar crop just landed: the new face appears in the chat
            // sidebar header and the media strip too, not only the hero. A full
            // reload is the cheap, honest way to update every surface at once —
            // it happens once per Keep. A pure portrait run (no crop) keeps the
            // smooth in-place hero swap.
            if (this.avatarWasPending && !data.failed) {
                this.stopped = true
                if (this.timeout) clearTimeout(this.timeout)
                window.location.reload()
                return
            }
            await this.swapHeroes()
            return
        }

        // Still generating — surface honest progress and keep polling.
        this.renderProgress(data)

        const elapsed = data.elapsed_seconds != null ? data.elapsed_seconds : this.elapsedSeconds()
        if (elapsed >= this.constructor.HARD_WALL) {
            this.showTimeout()
            return
        }
        this.scheduleNext(elapsed)
    }

    scheduleNext(elapsed) {
        if (this.stopped) return
        this.timeout = setTimeout(() => this.poll(), this.intervalFor(elapsed))
    }

    intervalFor(elapsed) {
        const step = this.constructor.SCHEDULE.find((s) => elapsed < s.until)
        return step ? step.every : this.constructor.SLOW_INTERVAL
    }

    elapsedSeconds() {
        return Math.round((Date.now() - this.startedAt) / 1000)
    }

    // Reflect real state into BOTH hero copies (main + sidebar): the evolving
    // partial frame, the phase word, and the elapsed clock.
    renderProgress(data) {
        const partial = data.candidate_partial_url
        if (partial && partial !== this.lastPartial) {
            this.lastPartial = partial
            document.querySelectorAll(".persona-card-partial-img").forEach((img) => {
                img.src = partial
                img.hidden = false
            })
        }

        const phase = this.phaseLabel(data.phase)
        document.querySelectorAll(".persona-portrait-phase").forEach((el) => {
            el.textContent = phase
        })

        const elapsed = data.elapsed_seconds != null ? data.elapsed_seconds : this.elapsedSeconds()
        const clock = this.formatElapsed(elapsed)
        document.querySelectorAll(".persona-portrait-elapsed").forEach((el) => {
            el.textContent = clock ? ` · ${clock}` : ""
        })
    }

    phaseLabel(phase) {
        switch (phase) {
            case "queued":
                return "Queued"
            case "cropping":
                return "Cropping avatar"
            case "painting":
            default:
                return "Painting"
        }
    }

    formatElapsed(seconds) {
        if (!seconds || seconds < 1) return ""
        if (seconds < 60) return `${seconds}s`
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return s ? `${m}m ${s}s` : `${m}m`
    }

    showTimeout() {
        this.stopped = true
        document.querySelectorAll(".persona-portrait-timeout").forEach((el) => {
            el.hidden = false
        })
    }

    // Fetch the freshly rendered hero for both copies and replace them in place.
    // Replacing outerHTML re-mounts this controller on the new main element;
    // since that element is no longer brewing, its connect() returns early — so
    // exactly one polling owner remains (here, none, the run is done).
    async swapHeroes() {
        this.stopped = true
        if (this.timeout) clearTimeout(this.timeout)
        try {
            const response = await fetch(this.frameUrlValue, { headers: { Accept: "application/json" } })
            const data = await response.json()
            // Sidebar first: it has no controller, so a plain replace is safe.
            const sidebar = document.querySelector(".persona-hero--sidebar")
            if (sidebar && data.sidebar) sidebar.outerHTML = data.sidebar
            // Main last: this replaces our own element and tears down this
            // controller instance.
            if (data.main) this.element.outerHTML = data.main
            else window.location.reload()
        } catch (error) {
            console.error("Persona hero swap error:", error)
            window.location.reload()
        }
    }
}
